import { z } from "zod";
import { type CompetitionFieldMessages, type CompetitorRef, enCompetitionFieldMessages, makePoolField, versionField } from "./common";
import type { GroupTiebreak } from "./match-format";
import {
	type IrmCode,
	type PhaseType,
	QUALIFICATION_MARKS,
	type QualificationMark,
	type ResultStatus,
	type StandingRule,
} from "./vocab";

/**
 * Progression — how a competitor gets from one phase to the next (slice S7):
 *
 *   GET  /phases/:id/standings     PhaseStandings        computed from the results, every read
 *   PUT  /phases/:id/competitors   PhaseCompetitorItem[] pools, seeds, tie order, marks by hand
 *   POST /phases/:id/advance       PhaseAdvanceResult    qualifiers into the next phase, re-runnable
 *   POST /phases/:id/fill-slots    SlotFillResult        "1st of Group A" ← the phase's standings
 *   POST /meets/:id/official       MeetOfficialResult    results official + "winner of QF1" filled
 *   POST /meets/:id/reopen         MeetReopenResult      back to UNOFFICIAL, what it filled un-filled
 *
 * ── What this replaces ──────────────────────────────────────────────────────
 * The predecessor's "Heat Summary" was either a meet filled with COPIES of the
 * heat rows (119 of 282 copies no longer matched their source) or a recompute
 * that found the heats by matching stage names. Finalists were re-typed with
 * no link to their heat, and winners were typed into the next match by hand.
 *
 * Here the standings are COMPUTED from the results on every read — nothing is
 * copied, so nothing drifts. Only DECISIONS are stored: who qualified and how
 * (`qualification`), and the order among competitors the results leave tied
 * (`tie_order`, after a swim-off or a countback). A slot that says "winner of
 * QF1" or "1st of Group A" is filled by the server, from its source.
 *
 * Every key is snake_case: these are wire shapes (CLAUDE.md, Naming). Exports
 * are prefixed (phase…, meetOfficial…, slotFill…): every competition module is
 * re-exported through one barrel, where two equal names are a compile error.
 */

// ─── Limits ──────────────────────────────────────────────────────────────────

/**
 * The most competitors one phase can hold as far as these endpoints go: the
 * entries of one `PUT /phases/:id/competitors`, the list of one advance, and
 * the rows of one standings table. A phase is bounded by its event's field;
 * the largest SEA Games field is a fraction of this.
 */
export const PHASE_FIELD_MAX = 500;

/** `seed` and `tie_order` are `smallint` columns: a larger number is a 422 here, not a Postgres error. */
const SMALLINT_MAX = 32_767;

/**
 * The audit headline a write may carry. The same rule as the API's own
 * `reasonField` (trimmed, 1–300); `contract-parity.progression.test.ts` pins
 * the two to one verdict, because they are typed in two places.
 */
const reasonText = z.string().trim().min(1).max(300);

/** A uuid naming an existing row. Lower-cased: Postgres prints uuids in lower case, so repeats are caught whatever the client sent. */
const idField = z.string().uuid().toLowerCase();

/** A seed within a phase, a tie order: 1 is first. */
const positiveSmallint = z.number().int().min(1).max(SMALLINT_MAX);

// ─── Messages ────────────────────────────────────────────────────────────────

/**
 * Every message the form schemas below emit, so a frontend can hand in
 * translated copy while the API keeps the English default verbatim (the
 * `makeCreateUserForm` pattern).
 */
export interface ProgressionFormMessages extends Pick<CompetitionFieldMessages, "poolFormat"> {
	/** The same competitor twice in one list. */
	competitorRepeated: string;
}

export const enProgressionFormMessages: ProgressionFormMessages = {
	poolFormat: enCompetitionFieldMessages.poolFormat,
	competitorRepeated: "Each competitor may appear only once",
};

/** Adds an issue on every element after the first that repeats its `competitor_id`. */
function flagRepeatedCompetitors(
	items: readonly { competitor_id: string }[],
	list: string,
	message: string,
	ctx: z.RefinementCtx,
): void {
	const seen = new Set<string>();
	items.forEach((item, index) => {
		if (seen.has(item.competitor_id)) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: [list, index, "competitor_id"], message });
		}
		seen.add(item.competitor_id);
	});
}

// ─── GET /phases/:id/standings ───────────────────────────────────────────────

/**
 * A boolean in a query string. Never `z.coerce.boolean()`: `Boolean("false")`
 * is true (CLAUDE.md, bug 2). Absent = false.
 */
const queryFlag = z
	.enum(["true", "false"])
	.optional()
	.transform((v) => v === "true");

/**
 * `GET /phases/:id/standings`
 *
 *   pool          one pool's table only ("A"; any case). The same rows, ranks
 *                 and order as the whole table filtered to that pool — the
 *                 ranking is always within a pool.
 *   round_no      only the meets of this round count: one discipline of a
 *                 decathlon, one round of golf.
 *   include_live  also count LIVE meets. By default only UNOFFICIAL and
 *                 OFFICIAL results count.
 */
export const phaseStandingsQuery = z.object({
	pool: makePoolField().optional(),
	round_no: z.coerce.number().int().min(1).max(SMALLINT_MAX).optional(),
	include_live: queryFlag,
});
export type PhaseStandingsQuery = z.input<typeof phaseStandingsQuery>;

/** The phase a standings table belongs to. */
export interface PhaseStandingsPhase {
	phase_id: string;
	event_id: string;
	type: PhaseType;
	name: string;
	standing_rule: StandingRule;
	/** The direction of THIS table (a decathlon ranks on points even though its 100 m is timed). */
	lower_is_better: boolean;
	advance_count: number | null;
	/** The first N of each meet go through by place (Q); null = all on results (q). */
	advance_by_place: number | null;
	/** How sides level on points are separated in a GROUP phase (contracts `groupTiebreakFor` of the event's sport). */
	tiebreak: GroupTiebreak;
}

/**
 * One competitor's line in a phase's standings, computed from the results of
 * the phase's counted meets: top-level meets only (a team tie's rubbers never
 * count twice), not CANCELLED, and UNOFFICIAL or OFFICIAL (LIVE too with
 * `include_live`). A competitor is listed when it holds a slot in a counted
 * meet, or has a `phase_competitors` row (drawn into a pool, advanced here).
 *
 *   played / won / drawn / lost   from `outcome`: played = WIN + DRAW + LOSS
 *                                 (a BYE is not a game)
 *   score_for                     their `result_value` summed over the meets
 *                                 that have an outcome (goals, sets, points
 *                                 scored); null with none
 *   score_against                 the OTHER slots' `result_value` in those meets
 *   points                        every `points` value summed — a forfeit with
 *                                 points still scores
 *   best_value / sum_value        the best / the sum of `result_value` over
 *                                 VALID results (`irm` empty); "best" follows
 *                                 the phase's `lower_is_better`
 *   irm                           the first invalid mark, in meet order
 *   qualification / tie_order     the decisions stored for this phase
 *
 * `standing_rank` follows the phase's `standing_rule` and is shared by ties:
 *
 *   MEET_RANK    the best `rank` in a meet
 *   RESULT_BEST  the best valid `result_value`
 *   RESULT_SUM   the sum of valid `result_value` — only with a valid result in
 *                every counted round of the pool (a meet's `round_no`; a meet
 *                without one is a round of its own); otherwise unranked
 *   POINTS_SUM   the sum of `points` — a competitor that played a counted meet
 *                with no points entered scores 0 (and is ranked); one with
 *                no counted meet at all has null points and no rank
 *   NONE         no ranking (a knock-out round): every rank is null
 *
 * Then, in a GROUP phase, the sport's tie-break (`PhaseStandingsPhase.tiebreak`):
 * SCORE = the score difference and the score for (higher is better); BWF =
 * `bwfGroupRanks` (head-to-head for two, then matches / games / points
 * difference). Then `tie_order`. Rows without a rank come after the ranked ones of
 * their pool: DNF / NM first, then DNS, then the rest.
 */
export interface PhaseStandingRow {
	competitor: CompetitorRef;
	/** The competitor's pool in this phase: its `phase_competitors` pool, else the pool of the meets it played. */
	pool: string | null;
	standing_rank: number | null;
	played: number;
	won: number;
	drawn: number;
	lost: number;
	score_for: number | null;
	score_against: number | null;
	points: number | null;
	best_value: number | null;
	sum_value: number | null;
	irm: IrmCode | null;
	qualification: QualificationMark | null;
	tie_order: number | null;
	/** The rank the results alone give (`standing_rank` before `tie_order`): rows of a pool that share it are tied by the results. */
	results_rank: number | null;
	/**
	 * BWF tie-break phases only (null elsewhere): games and points (rallies)
	 * won / lost over the counted matches — a team tie's summed over its
	 * rubbers.
	 */
	games_for: number | null;
	games_against: number | null;
	rallies_for: number | null;
	rallies_against: number | null;
}

/** `GET /phases/:id/standings` — rows by pool (no pool last), then rank. At most `PHASE_FIELD_MAX` rows. */
export interface PhaseStandings {
	phase: PhaseStandingsPhase;
	rows: PhaseStandingRow[];
}

// ─── PUT /phases/:id/competitors ─────────────────────────────────────────────

/**
 * One competitor's entry in the phase. A key that is ABSENT is left alone on
 * an existing entry; `null` clears it. A new entry starts with every absent
 * key empty, so `{ competitor_id }` alone just places the competitor in the
 * phase.
 *
 *   pool           the pool it is drawn into (any case in, stored upper-case)
 *   seed           its seed within this phase
 *   tie_order      the order among competitors the results leave TIED — the
 *                  outcome of a swim-off, a countback, a drawing of lots. 1 =
 *                  first of those tied. Not a rank: it cannot go stale when
 *                  someone above is disqualified.
 *   qualification  how it went through from this phase, set by hand
 */
const makePhaseEntryBody = (m: ProgressionFormMessages) =>
	z
		.object({
			competitor_id: idField,
			pool: makePoolField(m).nullable().optional(),
			seed: positiveSmallint.nullable().optional(),
			tie_order: positiveSmallint.nullable().optional(),
			qualification: z.enum(QUALIFICATION_MARKS).nullable().optional(),
		})
		.strict();

/**
 * `PUT /phases/:id/competitors` — upserts the listed competitors' entries in
 * the phase (1–`PHASE_FIELD_MAX`); entries not listed are left alone. Each
 * competitor must be entered in the phase's event. Refused under a locked
 * event. Answers the phase's entries, all of them.
 */
export function makePhaseCompetitorsBody(m: ProgressionFormMessages = enProgressionFormMessages) {
	return z
		.object({
			entries: z
				.array(makePhaseEntryBody(m))
				.min(1)
				.max(PHASE_FIELD_MAX)
				.superRefine((entries, ctx) => flagRepeatedCompetitors(entries, "entries", m.competitorRepeated, ctx)),
			/** The headline for this change in the Activity feed. */
			reason: reasonText.optional(),
		})
		.strict();
}
export const phaseCompetitorsBody = makePhaseCompetitorsBody();
export type PhaseCompetitorsBody = z.input<typeof phaseCompetitorsBody>;

/** One entry of a phase — what `PUT /phases/:id/competitors` answers, for every entry of the phase. */
export interface PhaseCompetitorItem {
	phase_competitor_id: string;
	competitor: CompetitorRef;
	pool: string | null;
	seed: number | null;
	tie_order: number | null;
	qualification: QualificationMark | null;
}

/** `details` of the 409 CONFLICT when an entry names a competitor that is not entered in the phase's event. Nothing was written. */
export interface PhaseEntriesConflictDetails {
	field: "entries";
	/** In the order sent. */
	competitor_ids: string[];
}

// ─── POST /phases/:id/advance ────────────────────────────────────────────────

/**
 * `POST /phases/:id/advance` — who goes through from THIS phase to
 * `to_phase_id` (a later phase of the same event), and how. 0–`PHASE_FIELD_MAX`
 * competitors.
 *
 * Declarative: send the WHOLE list each time. After a late disqualification,
 * send the corrected list and exactly the difference moves:
 *
 *  · listed competitors get their `qualification` here, and an entry in the
 *    next phase (with `seed`; an absent `seed` leaves an existing entry's
 *    seed alone);
 *  · competitors ADVANCED BEFORE — a mark here AND an entry in the next
 *    phase — that are no longer listed lose the mark and that entry, unless
 *    they already hold a slot in one of its meets: then both stay, and they
 *    are reported in `not_dropped` (take them off that start list, then
 *    advance again);
 *  · a mark here with no entry in the next phase (set by hand with
 *    `PUT /phases/:id/competitors` — a RESERVE, say) is not touched, and
 *    neither are entries in the next phase that never held a mark here
 *    (advanced from another phase, placed by hand).
 *
 * A listed competitor that is not an ENTERED competitor of this event is
 * reported in `rejected` and treated as not listed. Refused under a locked
 * event.
 */
export function makePhaseAdvanceBody(m: ProgressionFormMessages = enProgressionFormMessages) {
	return z
		.object({
			to_phase_id: idField,
			competitors: z
				.array(
					z
						.object({
							competitor_id: idField,
							qualification: z.enum(QUALIFICATION_MARKS),
							seed: positiveSmallint.nullable().optional(),
						})
						.strict(),
				)
				.max(PHASE_FIELD_MAX)
				.superRefine((competitors, ctx) => flagRepeatedCompetitors(competitors, "competitors", m.competitorRepeated, ctx)),
			/** The headline for this change in the Activity feed. */
			reason: reasonText.optional(),
		})
		.strict();
}
export const phaseAdvanceBody = makePhaseAdvanceBody();
export type PhaseAdvanceBody = z.input<typeof phaseAdvanceBody>;

/** `POST /phases/:id/advance` — what THIS call changed (a repeat of the same list answers empty lists). Ids are `competitor_id`s. */
export interface PhaseAdvanceResult {
	/** Given an entry in the next phase, in the order listed. */
	entered: string[];
	/** Their entry in the next phase removed (no longer listed). */
	dropped: string[];
	/** Their qualification mark here cleared (no longer listed). */
	marks_cleared: string[];
	/** Listed, but not an ENTERED competitor of this event — in the order listed. */
	rejected: string[];
	/** No longer listed, but already in a meet of the next phase: mark and entry left in place. */
	not_dropped: string[];
}

// ─── POST /meets/:id/official, POST /meets/:id/reopen ────────────────────────

/**
 * `POST /meets/:id/official` — makes a LIVE or UNOFFICIAL meet's results
 * OFFICIAL and fills every slot whose source is this meet, in one
 * transaction. `version` is the MEET's, as loaded.
 *
 *   "winner of" (WINNER)  the slot with outcome WIN or BYE — and a meet that
 *                         feeds WINNER / LOSER slots must have exactly one
 *                         (409 CONFLICT `{ outcome_missing: true }`)
 *   "loser of" (LOSER)    the slot with outcome LOSS
 *   "Nth of" (rank N)     the slot with rank N
 *
 * A slot in a meet that has already started (LIVE or beyond), or that holds
 * a result, is never rewritten: it is reported in `not_refilled`. When two
 * slots of one meet would take the same competitor (wired to "winner of QF1"
 * AND "1st of QF1"), nothing is written: 409 CONFLICT
 * `SlotFillConflictDetails` names them.
 */
export const meetOfficialBody = z
	.object({
		version: versionField,
		/** The headline for this change in the Activity feed. */
		reason: reasonText.optional(),
	})
	.strict();
export type MeetOfficialBody = z.input<typeof meetOfficialBody>;

/**
 * `POST /meets/:id/reopen` — OFFICIAL back to UNOFFICIAL. The slots this
 * meet's result filled are emptied again (their line-ups deleted) — unless one
 * is in a meet that has already started: then nothing changes, and the 409
 * CONFLICT names those meets (`{ started: [meet_id…] }`). `reason` is
 * required: reopening an official result is never routine.
 */
export const meetReopenBody = z
	.object({
		version: versionField,
		reason: reasonText,
	})
	.strict();
export type MeetReopenBody = z.input<typeof meetReopenBody>;

/** A slot this call filled (or emptied, on a reopen): `competitor_id` is the one placed (or taken out). */
export interface SlotFillChange {
	meet_competitor_id: string;
	meet_id: string;
	competitor_id: string;
}

/** `POST /meets/:id/official`. */
export interface MeetOfficialResult {
	meet_id: string;
	version: number;
	result_status: ResultStatus;
	/** Slots filled from this meet's result, by meet and slot. */
	filled: SlotFillChange[];
	/** `meet_competitor_id`s fed by this meet that were NOT rewritten: their meet has started, or the slot holds a result. */
	not_refilled: string[];
	/**
	 * `meet_competitor_id`s the result does not decide yet: no LOSS for "loser
	 * of", no rank N (or a shared one) for "Nth of", or the competitor already
	 * holds another slot of that meet. Left as they were.
	 */
	unresolved: string[];
	/**
	 * The phase's automatic advance, run when this meet was the last of its
	 * phase to go official (user, 2026-10-30: no Advance press for heats).
	 * Null when it did not run: the phase has no `advance_count`, no later
	 * phase, is not ranked on results across meets (RESULT_BEST / RESULT_SUM),
	 * or another of its meets is not official yet.
	 */
	auto_advance: MeetAutoAdvance | null;
}

/**
 * Why an automatic advance wrote nothing — the operator presses Advance:
 *   TIE_AT_CUT  more than `advance_count` share the places that go through
 *   BY_PLACE_OVER_COUNT  the first `advance_by_place` of each meet are more than `advance_count`
 *   POOLS       the phase is drawn in pools (how the places split is a decision)
 *   DECISIONS   a mark someone set by hand (by decision, a reserve) is stored
 */
export const AUTO_ADVANCE_SKIPS = ["TIE_AT_CUT", "BY_PLACE_OVER_COUNT", "POOLS", "DECISIONS"] as const;
export type AutoAdvanceSkip = (typeof AUTO_ADVANCE_SKIPS)[number];

/** `MeetOfficialResult.auto_advance`. Competitor ids are in standings order. */
export interface MeetAutoAdvance {
	to_phase_id: string;
	to_phase_name: string;
	/** `advance_count` of the phase. */
	advance_count: number;
	/** Null when the advance ran. */
	skipped: AutoAdvanceSkip | null;
	/** Newly entered in the next phase. */
	entered: string[];
	/** Taken out of the next phase (a corrected result moved them below the cut). */
	dropped: string[];
	/** Below the cut now, but kept: they already hold a slot in a meet of the next phase. */
	not_dropped: string[];
}

/** `POST /meets/:id/reopen`. */
export interface MeetReopenResult {
	meet_id: string;
	version: number;
	result_status: ResultStatus;
	/** The slots emptied again; `competitor_id` is the competitor taken out. */
	unfilled: SlotFillChange[];
}

/** `details` of the 409 CONFLICT when a reopen is refused. Nothing was written. */
export interface MeetReopenConflictDetails {
	/** Meets that have started (or hold a result) with a slot this meet filled. */
	started: string[];
}

// ─── POST /phases/:id/fill-slots ─────────────────────────────────────────────

/**
 * `POST /phases/:id/fill-slots` — fills the slots whose source is this phase
 * ("1st of Group A": `source_phase_id`, `source_pool`, `source_rank`) from the
 * phase's CURRENT standings — the same computation as `GET standings` (ranked
 * rows only; UNOFFICIAL and OFFICIAL results). A source with no pool takes
 * the rank among the rows that have no pool.
 *
 * Never automatic: an operator confirms the pool order (break ties with
 * `tie_order` first), then calls this. `pool` limits it to the slots fed by
 * that pool. Re-runnable: a slot already right is left alone. The same rule
 * as making a meet official: a slot in a meet that has started, or that holds
 * a result, is never rewritten; two slots of one meet that would take the
 * same competitor refuse the whole call (409 CONFLICT `SlotFillConflictDetails`).
 */
export function makeSlotFillBody(m: Pick<CompetitionFieldMessages, "poolFormat"> = enCompetitionFieldMessages) {
	return z
		.object({
			pool: makePoolField(m).optional(),
			/** The headline for this change in the Activity feed. */
			reason: reasonText.optional(),
		})
		.strict();
}
export const slotFillBody = makeSlotFillBody();
export type SlotFillBody = z.input<typeof slotFillBody>;

/** `POST /phases/:id/fill-slots`. The lists mean what they mean in `MeetOfficialResult`. */
export interface SlotFillResult {
	filled: SlotFillChange[];
	not_refilled: string[];
	/** No single competitor at that rank (nobody, or a tie not yet broken), or the competitor already holds another slot of that meet. */
	unresolved: string[];
}

/**
 * `details` of the 409 CONFLICT from `POST /meets/:id/official` and
 * `POST /phases/:id/fill-slots` when two (or more) slots of ONE meet would take
 * the same competitor from their sources — a wiring mistake in the bracket.
 * Nothing was written; correct the slots' sources, then call again.
 */
export interface SlotFillConflictDetails {
	/** The `meet_competitor_id`s that collide, by meet and slot. */
	duplicate_fill: string[];
}
